import { useState } from 'react';
import { CaretRightIcon, CircleNotchIcon, SparkleIcon } from '@phosphor-icons/react';

import classNames from '@/utils/classnames';

/**
 * Collapsed-by-default disclosure for a turn's reasoning (see `stores/reasoning.ts`).
 * Deliberately not run through {@link AssistantMarkdown} — plain, smaller, italic text keeps it
 * visually distinct from the answer so it reads as "notes", not the response.
 *
 * Collapsed even while streaming, unlike {@link AgentSteps}. Steps are a short list of what the
 * agent *did*; reasoning is an unbounded wall of the model talking to itself, and auto-opening it
 * pushed the answer off screen and made the thread jump as it grew. The header still says
 * "Thinking" with a spinner, so it is visible that reasoning is happening — it just doesn't take
 * the thread over to say so.
 */
export function ReasoningBlock({ text, streaming }: { text: string; streaming: boolean }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-1.5 self-start rounded-md py-0.5 text-xs font-medium text-fg-tertiary transition-colors hover:text-fg-secondary"
      >
        <CaretRightIcon
          size={12}
          weight="bold"
          className={classNames('shrink-0 transition-transform', open && 'rotate-90')}
        />
        <SparkleIcon size={12} className="shrink-0" />
        <span>{streaming ? 'Thinking' : 'Thought process'}</span>
        {streaming && <CircleNotchIcon size={12} className="animate-spin text-fg-tertiary" />}
      </button>
      {open && (
        <p className="border-l-2 border-line-subtle py-0.5 pl-3 text-xs leading-[1.6] whitespace-pre-wrap text-fg-tertiary italic">
          {text}
        </p>
      )}
    </div>
  );
}
